import { ANTIDOTE_ROUNDS, CARDS, CARD_MAGNITUDE, RULES } from '@horde/engine';
import type { Difficulty } from '@horde/engine';
import { worldMap } from '@horde/map-world';
import { useEffect } from 'react';
import { useStore } from '../store';

const DIFFS: Difficulty[] = ['calm', 'uneasy', 'outbreak', 'apocalypse'];
const pct = (x: number) => `${Math.round(x * 100)}%`;

/** One plain sentence per level, derived from the preset. */
function describeDifficulty(d: Difficulty): string {
  const r = RULES[d];
  const spawnOdds = Math.round(1 / r.baseSpawn);
  const biteOdds = 7 - r.biteThreshold; // faces that hit on a d6
  const growth =
    r.hordeGrowthRate <= 0.15
      ? 'slowly'
      : r.hordeGrowthRate <= 0.2
        ? 'steadily'
        : r.hordeGrowthRate <= 0.25
          ? 'quickly'
          : 'explosively';
  const wave =
    r.assaultFraction <= 0.15 ? 'a trickle' : r.assaultFraction <= 0.25 ? 'a wave' : 'a flood';
  return (
    `A clean territory of yours has about a 1 in ${spawnOdds} chance of sprouting a zombie each turn. ` +
    `Each zombie bite lands ${biteOdds} time${biteOdds === 1 ? '' : 's'} in 6. ` +
    `Horde territories grow ${growth}, and every ${r.spreadEvery} rounds each one sends ${wave} into a neighbour. ` +
    `The horde starts with ${pct(r.hordeShare)} of a player's units.`
  );
}

/** Player-facing rules, built from the engine's own constants. */
export function Rules({ onClose }: { onClose: () => void }) {
  const game = useStore((s) => s.game);
  const difficulty = game?.config.difficulty ?? 'outbreak';
  const r = RULES[difficulty];

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="overlay" onClick={onClose} role="dialog" aria-label="Rules">
      <div className="rules" onClick={(e) => e.stopPropagation()}>
        <header className="rules__head">
          <h2>How to play</h2>
          <button className="btn btn--ghost btn--sm" onClick={onClose} aria-label="Close rules">
            ✕
          </button>
        </header>
        <div className="rules__body">
          <section>
            <h3>Goal</h3>
            <p>
              <b>Domination:</b> be the last faction standing while holding at least half the map (
              {Math.ceil(worldMap.territories.length * r.dominationShare)} of{' '}
              {worldMap.territories.length} territories). Outliving everyone is not enough if the
              horde holds the world.
            </p>
            <p>
              <b>Eradication</b> (optional, always on when playing alone): win the moment the last
              zombie is destroyed.
            </p>
            <p>
              <b>Everyone loses</b> if every faction is wiped out.
            </p>
          </section>

          <section>
            <h3>Your turn</h3>
            <ol>
              <li>
                <b>Reinforce.</b> You receive the larger of {r.minReinforcements} or your territory
                count ÷ {r.territoriesPerReinforcement} (rounded down), plus a bonus for each region
                you control entirely. Place them on territories you own.
                <ul className="rules__regions">
                  {worldMap.regions.map((reg) => (
                    <li key={reg.id}>
                      {reg.name} <span className="num">+{reg.bonus}</span>{' '}
                      <span className="muted">({reg.territories.length} territories)</span>
                    </li>
                  ))}
                </ul>
              </li>
              <li>
                <b>Draw.</b> You must draw a card. Instant cards resolve at once, good or bad. Hold
                cards go to your hand (limit {r.handLimit}) and can be played on any of your turns.
                Later in the game you draw more than one.
              </li>
              <li>
                <b>Attack.</b> Attack any adjacent territory you don't own from one with 2+ units.
                Roll up to 3 dice against up to 2. Highest die against highest die; ties go to the
                defender. When you capture a territory, at least as many units as dice you rolled
                move in.
                <br />
                <b>Purge:</b> attack the zombies inside your own territory the same way. Every unit
                there can roll.
                <br />
                <b>Pinned:</b> a territory with as many zombies as units cannot attack out. Purge
                first.
              </li>
              <li>
                <b>Fortify.</b> Optionally move units once along a chain of your own territories,
                then end your turn.
              </li>
            </ol>
          </section>

          <section>
            <h3>Zombie Rising</h3>
            <p>At the end of your turn, in each territory you own:</p>
            <ul>
              <li>
                <b>Spawn.</b> Chance of {pct(r.baseSpawn)} plus {pct(r.spawnPerZombie)} per zombie
                already there (max {pct(r.spawnCap)}). A spawn adds one zombie plus{' '}
                {pct(r.spawnBurst)} of those present. Infestations snowball.
              </li>
              <li>
                <b>Bite.</b> Each zombie, up to your unit count, rolls a die. {r.biteThreshold}+
                kills a unit.
              </li>
              <li>
                <b>Overrun.</b> If zombies outnumber your units, the territory falls to the horde.
              </li>
            </ul>
            <p>
              After the last player's turn, every horde territory grows by {pct(r.hordeGrowthRate)}{' '}
              (at least {r.hordeGrowth}). Every {r.spreadEvery} rounds each horde stack pushes{' '}
              {pct(r.assaultFraction)} of itself into a neighbouring territory.
            </p>
          </section>

          <section>
            <h3>Escalation</h3>
            <p>
              Card effects are multiplied by <b>×m</b>, shown next to the round number. It starts at
              1 and grows by one every {r.escalationEvery} rounds, up to ×{r.escalationCap}. Every{' '}
              {r.drawEscalationEvery} rounds you draw one more card per turn.
            </p>
          </section>

          <section>
            <h3>Cards</h3>
            <table className="rules__cards">
              <tbody>
                {CARDS.map((c) => (
                  <tr key={c.id} className={`is-${c.polarity}`}>
                    <td>
                      {c.name} <span className="muted num">×{c.copies}</span>
                    </td>
                    <td className="muted">{c.kind}</td>
                    <td>
                      {c.description.replace('{n}', String(CARD_MAGNITUDE[c.id] ?? ''))}
                      {c.id === 'antidote' ? ` (${ANTIDOTE_ROUNDS} rounds)` : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="muted">Base numbers shown; they scale with ×m.</p>
          </section>

          <section>
            <h3>Difficulty</h3>
            <p>Difficulty changes how fast the dead multiply, not how the game is played.</p>
            <ul className="rules__difficulty">
              {DIFFS.map((d) => (
                <li key={d} className={d === difficulty ? 'is-current' : ''}>
                  <b style={{ textTransform: 'capitalize' }}>{d}</b>
                  {d === difficulty ? <span className="tag">this game</span> : null}
                  <span>{describeDifficulty(d)}</span>
                </li>
              ))}
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
}
